import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

export type ApiPermissionLevel = "read" | "write" | "admin";

export interface ApiKey {
  id: string;
  user_id: string;
  name: string;
  key_prefix: string;
  permissions: string[];
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
  revoked_at: string | null;
}

export interface CreateApiKeyData {
  name: string;
  permissions?: ApiPermissionLevel[];
  expires_at?: string;
}

// Keys grant service-role access through the `api` function: they must come
// from the CSPRNG, not Math.random (predictable). 62 chars ⇒ reject bytes
// ≥ 248 to keep the distribution uniform.
function generateApiKey(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "lv_prod_";
  while (result.length < 8 + 32) {
    for (const b of crypto.getRandomValues(new Uint8Array(64))) {
      if (b < 248 && result.length < 8 + 32) result += chars[b % chars.length];
    }
  }
  return result;
}

async function hashKey(key: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(key);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function useApiKeys() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["api-keys", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("api_keys")
        .select("*")
        .is("revoked_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as ApiKey[];
    },
    enabled: !!user?.id,
  });
}

export function useCreateApiKey() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (data: CreateApiKeyData) => {
      if (!user?.id) throw new Error("Usuário não autenticado");
      const fullKey = generateApiKey();
      const keyHash = await hashKey(fullKey);
      const keyPrefix = fullKey.substring(0, 12);
      const { error } = await supabase.from("api_keys").insert({
        user_id: user.id,
        name: data.name,
        key_hash: keyHash,
        key_prefix: keyPrefix,
        permissions: data.permissions || ["read"],
        expires_at: data.expires_at || null,
      });
      if (error) throw error;
      return { fullKey, keyPrefix };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys", user?.id] });
      toast.success("API Key criada com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao criar API Key: " + error.message);
    },
  });
}

export function useRevokeApiKey() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (keyId: string) => {
      const { error } = await supabase
        .from("api_keys")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", keyId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys", user?.id] });
      toast.success("API Key revogada!");
    },
    onError: (error) => {
      toast.error("Erro ao revogar API Key: " + error.message);
    },
  });
}
